/**
 * This file is primarily responsible for exporting `useCallsTableColumns` which is a hook that
 * returns the columns for the calls table.
 */

import {
  GridColDef,
  GridColumnGroup,
  GridColumnGroupingModel,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import {LoadingDots} from '@wandb/weave/components/LoadingDots';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {UserLink} from '@wandb/weave/components/UserLink';
import {convertBytes} from '@wandb/weave/util';
import React, {
  FC,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import {TEAL_600} from '../../../../../../common/css/color.styles';
import {useDeepMemo} from '../../../../../../common/state/hooks';
import {monthRoundedTime} from '../../../../../../common/util/time';
import {isWeaveObjectRef, parseRef} from '../../../../../../react';
import {makeRefCall} from '../../../../../../util/refs';
import {Timestamp} from '../../../../../Timestamp';
import {CellValue} from '../../../Browse2/CellValue';
import {CellValueRun} from '../../../Browse2/CellValueRun';
import {TableRowSelectionContext} from '../../../TableRowSelectionContext';
import {RunColorsContext} from '../../context';
import {
  convertFeedbackFieldToBackendFilter,
  parseFeedbackType,
} from '../../feedback/HumanFeedback/tsHumanFeedback';
import {
  convertScorerFeedbackFieldToBackendFilter,
  parseScorerFeedbackField,
  RUNNABLE_FEEDBACK_IN_SUMMARY_PREFIX,
  RUNNABLE_FEEDBACK_OUTPUT_PART,
} from '../../feedback/HumanFeedback/tsScorerFeedback';
import {Reactions} from '../../feedback/Reactions';
import {
  CellFilterWrapper,
  OnUpdateFilter,
} from '../../filters/CellFilterWrapper';
import {isWeaveRef} from '../../filters/common';
import {
  getCostsFromCellParams,
  getTokensFromCellParams,
} from '../CallPage/cost';
import {isEvaluateOp} from '../common/heuristics';
import {CallLink} from '../common/Links';
import {StatusChip} from '../common/StatusChip';
import {
  buildDynamicColumnsWithDepth,
  TreeBuildingCache,
} from '../common/tabularListViews/columnBuilderWithDepth';
import {ComputedCallStatuses} from '../wfReactInterface/traceServerClientTypes';
import {
  convertISOToDate,
  flattenedTraceCallStatusCode,
  traceCallLatencyMs,
  traceCallLatencyS,
} from '../wfReactInterface/tsDataModelHooks';
import {opVersionRefOpName} from '../wfReactInterface/utilities';
import {FlattenedCallData, TRACE_NAME_FIELD} from './CallsTable';
import {
  insertPath,
  isDynamicCallColumn,
  Path,
  pathToString,
  stringToPath,
} from './callsTableColumnsUtil';
import {deriveLLMChatCompletionColumnsAndGroups} from './CallsTableDerivedColumns/LLMChatCompletions';
import {WFHighLevelCallFilter} from './callsTableFilter';
import {OpVersionIndexText} from './OpVersionIndexText';

// Component for rendering wb_run_id cell with optional colored dot
const RunIdCellWithColor: React.FC<{
  runId: string | undefined;
  onUpdateFilter?: OnUpdateFilter;
  rowId: string;
}> = ({runId, onUpdateFilter, rowId}) => {
  const runColorsContext = useContext(RunColorsContext);

  if (runId == null) {
    return null;
  }
  const parts = runId.split('/');
  if (parts.length !== 3) {
    return <span>{runId}</span>;
  }
  const [entityName, projectName, runName] = parts;

  // Get run color if context is available, this is only available for Weave Panels in Workspace,
  // the primary weave entrypoint does not have runs data
  const runColor = runColorsContext?.getRunColor?.(runName);

  return (
    <div className="flex items-center gap-1.5">
      {runColor && (
        <div
          className="h-12 w-12 rounded-full flex-shrink-0 mr-6"
          style={{backgroundColor: runColor}}
        />
      )}
      <CellValueRun
        entity={entityName}
        project={projectName}
        run={runName}
        onUpdateFilter={onUpdateFilter}
        rowId={rowId}
      />
    </div>
  );
};

const HIDDEN_DYNAMIC_COLUMN_FIELDS = ['summary.views'];

const HIDDEN_DYNAMIC_COLUMN_PREFIXES = [
  'summary.views',
  'summary.status_counts',
  'summary.usage',
  'summary.weave',
  'feedback',
];

export const useCallsTableColumns = (
  entity: string,
  project: string,
  effectiveFilter: WFHighLevelCallFilter,
  currentViewId: string,
  tableData: FlattenedCallData[],
  expandedRefCols: Set<string>,
  onCollapse: (col: string) => void,
  onExpand: (col: string) => void,
  columnIsRefExpanded: (col: string) => boolean,
  allowedColumnPatterns?: string[],
  onUpdateFilter?: OnUpdateFilter,
  costsLoading: boolean = false,
  costsHasError: boolean = false,
  includeTotalStorageSizeBytes: boolean = false,
  storageSizeLoading: boolean = false,
  storageHasError: boolean = false,
  maxHeaderDepth: number = 2,
  cache: TreeBuildingCache = new Map(),
  maxColumns?: number,
  ioLoading: boolean = false
) => {
  const [userDefinedColumnWidths, setUserDefinedColumnWidths] = useState<
    Record<string, number>
  >({});

  // Determine which columns have refs to expand. Followup: this might want
  // to be an ever-growing list. Instead, this is recalculated on each page.
  // This is used to determine which columns should be expandable / collapsible.
  const columnsWithRefs = useMemo(() => {
    const refColumns = new Set<string>();
    tableData.forEach(row => {
      Object.keys(row).forEach(key => {
        const value = row[key];
        if (value && typeof value === 'string' && refIsExpandable(value)) {
          refColumns.add(key);
        }
      });
    });

    return refColumns;
  }, [tableData]);

  const shouldIgnoreColumn = useCallback(
    (col: string) => {
      const columnsWithRefsList = Array.from(columnsWithRefs);
      // Captures the case where the column has just been expanded.
      for (const refCol of columnsWithRefsList) {
        if (col.startsWith(refCol + '.')) {
          return true;
        }
      }
      return false;
    },
    [columnsWithRefs]
  );

  // If either of these values has changed we'll reset the dynamic columns.
  const resetDep = {effectiveFilter, currentViewId};
  const allDynamicColumnNames = useAllDynamicColumnNames(
    tableData,
    shouldIgnoreColumn,
    resetDep,
    maxColumns
  );

  // Determine what sort of view we are looking at based on the filter
  const isSingleOpVersion = useMemo(
    () => effectiveFilter.opVersionRefs?.length === 1,
    [effectiveFilter.opVersionRefs]
  );
  const isSingleOp = useMemo(
    () =>
      effectiveFilter.opVersionRefs?.length === 1 &&
      effectiveFilter.opVersionRefs[0].includes(':*'),
    [effectiveFilter.opVersionRefs]
  );
  const preservePath = useMemo(
    () =>
      effectiveFilter.opVersionRefs?.length === 1 &&
      effectiveFilter.opVersionRefs[0].includes('predict_and_score:'),
    [effectiveFilter.opVersionRefs]
  );

  // TODO: Make this pluggable
  const {derivedColumns, derivedGroups} = useMemo(() => {
    return deriveLLMChatCompletionColumnsAndGroups(tableData);
  }, [tableData]);

  const columns = useMemo(
    () =>
      buildCallsTableColumns(
        entity,
        project,
        preservePath,
        isSingleOp,
        isSingleOpVersion,
        allDynamicColumnNames,
        expandedRefCols,
        onCollapse,
        columnsWithRefs,
        onExpand,
        userDefinedColumnWidths,
        allowedColumnPatterns,
        onUpdateFilter,
        costsLoading,
        costsHasError,
        includeTotalStorageSizeBytes
          ? {
              storageSizeLoading,
            }
          : null,
        storageHasError,
        maxHeaderDepth,
        cache,
        derivedColumns,
        derivedGroups,
        ioLoading
      ),
    [
      entity,
      project,
      preservePath,
      isSingleOp,
      isSingleOpVersion,
      allDynamicColumnNames,
      expandedRefCols,
      onCollapse,
      columnsWithRefs,
      onExpand,
      userDefinedColumnWidths,
      allowedColumnPatterns,
      onUpdateFilter,
      costsLoading,
      includeTotalStorageSizeBytes,
      storageSizeLoading,
      storageHasError,
      costsHasError,
      maxHeaderDepth,
      cache,
      derivedColumns,
      derivedGroups,
      ioLoading,
    ]
  );

  return useMemo(() => {
    return {
      columns,
      setUserDefinedColumnWidths,
      columnsWithRefs,
    };
  }, [columns, setUserDefinedColumnWidths, columnsWithRefs]);
};

const CallLinkCell: FC<{
  rowParams: GridRenderCellParams<FlattenedCallData>;
  entity: string;
  project: string;
  preservePath: boolean;
}> = ({rowParams, entity, project, preservePath}) => {
  const {getDescendantCallIdAtSelectionPath} = useContext(
    TableRowSelectionContext
  );

  const traceCall = rowParams.row.__traceCall__;

  const opName =
    traceCall.display_name ??
    opVersionRefOpName(traceCall.op_name) ??
    traceCall.op_name;
  const isEval = isEvaluateOp(opVersionRefOpName(traceCall.op_name));

  return (
    <CallLink
      entityName={entity}
      projectName={project}
      opName={opName}
      callId={traceCall.id}
      fullWidth={true}
      color={TEAL_600}
      isEval={isEval}
      focusedCallId={
        preservePath
          ? (getDescendantCallIdAtSelectionPath?.(traceCall.id) ?? undefined)
          : undefined
      }
    />
  );
};

function buildCallsTableColumns(
  entity: string,
  project: string,
  preservePath: boolean,
  isSingleOp: boolean,
  isSingleOpVersion: boolean,
  allDynamicColumnNames: string[],
  expandedRefCols: Set<string>,
  onCollapse: (col: string) => void,
  columnsWithRefs: Set<string>,
  onExpand: (col: string) => void,
  userDefinedColumnWidths: Record<string, number>,
  allowedColumnPatterns?: string[],
  onUpdateFilter?: OnUpdateFilter,
  costsLoading: boolean = false,
  costsHasError: boolean = false,
  storageSizeInfo: {
    storageSizeLoading: boolean;
  } | null = null,
  storageHasError: boolean = false,
  maxHeaderDepth: number = 2,
  cache: TreeBuildingCache = new Map(),
  derivedColumns: Array<GridColDef<FlattenedCallData>> = [],
  derivedGroups: GridColumnGroupingModel = [],
  ioLoading: boolean = false
): {
  cols: Array<GridColDef<FlattenedCallData>>;
  colGroupingModel: GridColumnGroupingModel;
} {
  // Filters summary.usage. because we add a derived column for tokens and cost
  // Sort attributes after inputs and outputs.
  const filteredDynamicColumnNames = allDynamicColumnNames
    .filter(name => !HIDDEN_DYNAMIC_COLUMN_FIELDS.includes(name))
    .filter(
      name =>
        !HIDDEN_DYNAMIC_COLUMN_PREFIXES.some(prefix =>
          name.startsWith(prefix + '.')
        )
    )
    .sort((a, b) => {
      const prefixes = ['inputs.', 'output.', 'attributes.'];
      const aPrefix =
        a === 'output'
          ? 'output.'
          : (prefixes.find(p => a.startsWith(p)) ?? '');
      const bPrefix =
        b === 'output'
          ? 'output.'
          : (prefixes.find(p => b.startsWith(p)) ?? '');
      if (aPrefix !== bPrefix) {
        return prefixes.indexOf(aPrefix) - prefixes.indexOf(bPrefix);
      }
      return a.localeCompare(b);
    });

  const cols: Array<GridColDef<FlattenedCallData>> = [
    {
      field: TRACE_NAME_FIELD,
      headerName: 'Trace',
      minWidth: 100,
      width: 250,
      hideable: false,
      display: 'flex',
      valueGetter: (_: any, row: any) => {
        const op_name = row.op_name;
        if (!isWeaveRef(op_name)) {
          return op_name;
        }
        return opVersionRefOpName(op_name);
      },
      renderCell: rowParams => {
        const name: string | undefined =
          rowParams.row.__traceCall__.display_name ??
          rowParams.row.__traceCall__.summary?.weave?.trace_name;
        return (
          <CellFilterWrapper
            onUpdateFilter={onUpdateFilter}
            field={TRACE_NAME_FIELD}
            rowId={rowParams.id.toString()}
            operation={'(string): equals'}
            value={name}
            style={{
              display: 'flex',
              width: '100%',
            }}>
            <CallLinkCell
              rowParams={rowParams}
              entity={entity}
              project={project}
              preservePath={preservePath}
            />
          </CellFilterWrapper>
        );
      },
    },
    {
      field: 'feedback',
      headerName: 'Feedback',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (rowParams: GridRenderCellParams) => {
        const rowIndex = rowParams.api.getRowIndexRelativeToVisibleRows(
          rowParams.id
        );
        const callId = rowParams.row.__traceCall__.id;
        const weaveRef = makeRefCall(entity, project, callId);
        return (
          <Reactions
            weaveRef={weaveRef}
            forceVisible={rowIndex === 0}
            twWrapperStyles={{
              width: '100%',
              height: '100%',
            }}
          />
        );
      },
    },
    ...(isSingleOp && !isSingleOpVersion
      ? [
          {
            field: 'derived.op_version',
            headerName: 'Op Version',
            type: 'number' as const,
            align: 'right' as const,
            disableColumnMenu: true,
            sortable: false,
            filterable: false,
            resizable: false,
            renderCell: (cellParams: any) => (
              <OpVersionIndexText opVersionRef={cellParams.row.op_name} />
            ),
          },
        ]
      : []),
    {
      field: 'summary.weave.status',
      headerName: 'Status',
      headerAlign: 'center',
      sortable: true,
      // disableColumnMenu: true,
      resizable: false,
      width: 59,
      display: 'flex',
      valueGetter: (_: any, row: any) => {
        return flattenedTraceCallStatusCode(row);
      },
      renderCell: cellParams => {
        const valueStatus = flattenedTraceCallStatusCode(cellParams.row);
        const valueFilter = ComputedCallStatuses[valueStatus];
        return (
          <CellFilterWrapper
            onUpdateFilter={onUpdateFilter}
            field="summary.weave.status"
            rowId={cellParams.id.toString()}
            operation={'(string): in'}
            value={valueFilter}>
            <div style={{margin: 'auto'}}>
              <StatusChip value={valueStatus} iconOnly />
            </div>
          </CellFilterWrapper>
        );
      },
    },
  ];

  const groupingModel: GridColumnGroupingModel = [];

  // Extend the columns with the derived columns
  cols.push(...derivedColumns);
  groupingModel.push(...derivedGroups);

  if (!ioLoading || filteredDynamicColumnNames.length > 0) {
    const {
      cols: newCols,
      groupingModel: newGroupingModel,
    }: {
      cols: Array<GridColDef<FlattenedCallData>>;
      groupingModel: GridColumnGroupingModel;
    } = buildDynamicColumnsWithDepth<FlattenedCallData>(
      filteredDynamicColumnNames,
      row => {
        const [rowEntity, rowProject] = row.__traceCall__.project_id.split('/');
        return {entity: rowEntity, project: rowProject};
      },
      (row, key) => row[key],
      cache,
      key => expandedRefCols.has(key),
      key => columnsWithRefs.has(key),
      onCollapse,
      onExpand,
      // TODO (Tim) - (BackendExpansion): This can be removed once we support backend expansion!
      key => !columnsWithRefs.has(key),
      onUpdateFilter,
      maxHeaderDepth,
      ioLoading
    );
    cols.push(...newCols);
    groupingModel.push(...newGroupingModel);
  } else {
    cols.push({
      field: 'inputs_loading',
      headerName: 'inputs',
      filterable: false,
      sortable: false,
      flex: 1,
      renderCell: () => {
        return <LoadingDots />;
      },
    });
    cols.push({
      field: 'outputs_loading',
      headerName: 'output',
      filterable: false,
      sortable: false,
      flex: 1,
      renderCell: () => {
        return <LoadingDots />;
      },
    });
    if (maxHeaderDepth > 1) {
      let group: GridColumnGroup = {
        groupId: 'io_loading',
        headerName: '',
        children: [{field: 'inputs_loading'}, {field: 'outputs_loading'}],
      };
      for (let i = 3; i < maxHeaderDepth; i++) {
        group = {
          groupId: `io_loading.${i}`,
          headerName: '',
          children: [group],
        };
      }
      groupingModel.push(group);
    }
  }

  // Create special feedback columns with grouping model
  const annotationColNames = allDynamicColumnNames.filter(
    c =>
      c.startsWith('summary.weave.feedback.wandb.annotation') &&
      c.endsWith('payload.value')
  );
  if (annotationColNames.length > 0) {
    // Handle annotation columns based on header depth
    if (maxHeaderDepth === 1) {
      // For Header Depth == 1, skip the "Annotations" group header and add leaf nodes directly
      const annotationColumns: Array<GridColDef<FlattenedCallData>> =
        annotationColNames.map(c => {
          const parsed = parseFeedbackType(c);
          const fieldName = parsed
            ? parsed.userDefinedType
            : c.split('.').pop() || c;
          return {
            field: convertFeedbackFieldToBackendFilter(c),
            headerName: `annotations.${fieldName}`,
            width: 150,
            minWidth: 150,
            flex: 1,
            renderHeader: () => {
              const content = (
                <div className="flex items-center gap-1">
                  <span>...</span>
                  <span>{fieldName}</span>
                </div>
              );

              return (
                <Tooltip
                  content={
                    <div className="font-mono text-xs">
                      annotations.{fieldName}
                    </div>
                  }
                  side="bottom"
                  trigger={content}
                />
              );
            },
            valueGetter: (_: any, row: any) => {
              return row[c];
            },
            renderCell: (params: GridRenderCellParams<FlattenedCallData>) => {
              return <CellValue value={params.value} />;
            },
          };
        });
      cols.push(...annotationColumns);
    } else {
      // For Header Depth > 1, use the normal grouped structure
      groupingModel.push({
        groupId: 'feedback',
        headerName: 'annotations',
        children: annotationColNames.map(col => ({
          field: convertFeedbackFieldToBackendFilter(col),
        })),
      });

      // Add feedback columns
      const annotationColumns: Array<GridColDef<FlattenedCallData>> =
        annotationColNames.map(c => {
          const parsed = parseFeedbackType(c);
          return {
            field: convertFeedbackFieldToBackendFilter(c),
            headerName: parsed ? parsed.displayName : `${c}`,
            width: 150,
            minWidth: 150,
            flex: 1,
            renderHeader: () => {
              return <div>{parsed ? parsed.userDefinedType : c}</div>;
            },
            valueGetter: (_: any, row: any) => {
              return row[c];
            },
            renderCell: (params: GridRenderCellParams<FlattenedCallData>) => {
              return <CellValue value={params.value} />;
            },
          };
        });
      cols.push(...annotationColumns);
    }
  }

  const scoreColNames = allDynamicColumnNames.filter(
    c =>
      c.startsWith(RUNNABLE_FEEDBACK_IN_SUMMARY_PREFIX) &&
      c.includes(RUNNABLE_FEEDBACK_OUTPUT_PART)
  );
  if (scoreColNames.length > 0) {
    // Group scores by scorer name and nested paths
    const scorerGroups = new Map<string, Map<string, string[]>>();
    scoreColNames.forEach(colName => {
      const parsed = parseScorerFeedbackField(colName);
      if (parsed) {
        const scorerName = parsed.scorerName;
        const pathParts = parsed.scorePath.replace(/^\./, '').split('.');
        // Only create a group path if there are multiple parts
        const groupPath =
          pathParts.length > 1 ? pathParts.slice(0, -1).join('.') : '';

        if (!scorerGroups.has(scorerName)) {
          scorerGroups.set(scorerName, new Map());
        }
        const scorerGroup = scorerGroups.get(scorerName)!;
        if (!scorerGroup.has(groupPath)) {
          scorerGroup.set(groupPath, []);
        }
        scorerGroup.get(groupPath)!.push(colName);
      }
    });

    // Create scorer groups in the grouping model for each scorer
    const scoreGroup = {
      groupId: 'scores',
      headerName: 'Scores',
      children: Array.from(scorerGroups.entries()).map(
        ([scorerName, pathGroups]) => {
          const scorerGroupChildren = Array.from(pathGroups.entries())
            .filter(([groupPath, _]) => groupPath !== '') // Filter out non-grouped fields
            .map(([groupPath, _]) => ({
              groupId: `scores.${scorerName}.${groupPath}`,
              headerName: groupPath,
              children: [] as any[],
            }));

          return {
            groupId: `scores.${scorerName}`,
            headerName: scorerName,
            children: scorerGroupChildren,
          };
        }
      ),
    };
    groupingModel.push(scoreGroup);

    // Create columns for each scorer's fields
    const scoreColumns: Array<GridColDef<FlattenedCallData>> = [];
    scorerGroups.forEach((pathGroups, scorerName) => {
      pathGroups.forEach((colNames, groupPath) => {
        const scorerGroup = groupPath
          ? scoreGroup.children
              .find(g => g.groupId === `scores.${scorerName}`)
              ?.children.find(
                g => g.groupId === `scores.${scorerName}.${groupPath}`
              )
          : scoreGroup.children.find(g => g.groupId === `scores.${scorerName}`);

        colNames.forEach(colName => {
          const parsed = parseScorerFeedbackField(colName);
          const field = convertScorerFeedbackFieldToBackendFilter(colName);
          if (parsed === null) {
            scoreColumns.push({
              field,
              headerName: colName,
              width: 150,
              renderHeader: () => {
                return <div>{colName}</div>;
              },
              valueGetter: (_: any, row: any) => {
                return row[colName];
              },
              renderCell: (params: GridRenderCellParams<FlattenedCallData>) => {
                return <CellValue value={params.value} />;
              },
            });
            return;
          }

          // Add to scorer's group
          scorerGroup?.children.push({field});

          const leafName =
            parsed.scorePath.split('.').pop()?.replace(/^\./, '') ||
            parsed.scorePath;

          scoreColumns.push({
            field,
            headerName: `Scores.${parsed.scorerName}${parsed.scorePath}`,
            width: 150,
            renderHeader: () => {
              return <div>{leafName}</div>;
            },
            valueGetter: (_: any, row: any) => {
              return row[colName];
            },
            renderCell: (params: GridRenderCellParams<FlattenedCallData>) => {
              return (
                <CellFilterWrapper
                  onUpdateFilter={onUpdateFilter}
                  field={field}
                  rowId={params.id.toString()}
                  operation={null}
                  value={params.value}
                  style={{
                    height: '100%',
                    width: '100%',
                  }}>
                  <CellValue value={params.value} />
                </CellFilterWrapper>
              );
            },
          });
        });
      });
    });
    cols.push(...scoreColumns);
  }

  cols.push({
    field: 'wb_user_id',
    headerName: 'User',
    headerAlign: 'center',
    width: 50,
    align: 'center',
    sortable: false,
    resizable: false,
    display: 'flex',
    renderCell: cellParams => {
      const userId = cellParams.row.__traceCall__.wb_user_id;
      if (userId == null) {
        return null;
      }
      return (
        <CellFilterWrapper
          onUpdateFilter={onUpdateFilter}
          field="wb_user_id"
          rowId={cellParams.id.toString()}
          operation="(string): equals"
          value={userId}>
          <UserLink userId={userId} />
        </CellFilterWrapper>
      );
    },
  });

  const startedAtCol: GridColDef<FlattenedCallData> = {
    field: 'started_at',
    headerName: 'Called',
    sortable: true,
    sortingOrder: ['desc', 'asc'],
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    renderCell: cellParams => {
      // TODO: A better filter might be to be on the same day?
      const date = convertISOToDate(cellParams.row.__traceCall__.started_at);
      const filterDate = new Date(date);
      filterDate.setSeconds(0, 0);
      const filterValue = filterDate.toISOString();
      const value = date.getTime() / 1000;
      return (
        <CellFilterWrapper
          onUpdateFilter={onUpdateFilter}
          field="started_at"
          rowId={cellParams.id.toString()}
          operation="(date): after"
          value={filterValue}>
          <Timestamp value={value} format="relative" />
        </CellFilterWrapper>
      );
    },
  };
  cols.push(startedAtCol);

  cols.push({
    field: 'tokens',
    headerName: 'Tokens',
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    align: 'right',
    headerAlign: 'right',
    // Should probably have a custom filter here.
    filterable: false,
    sortable: false,
    valueGetter: (_: any, row: any) => {
      const {tokensNum} = getTokensFromCellParams(row);
      return tokensNum;
    },
    renderCell: cellParams => {
      const {tokens, tokenToolTipContent} = getTokensFromCellParams(
        cellParams.row
      );
      return (
        <Tooltip trigger={<div>{tokens}</div>} content={tokenToolTipContent} />
      );
    },
  });
  cols.push({
    field: 'cost',
    headerName: 'Cost',
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    align: 'right',
    headerAlign: 'right',
    // Should probably have a custom filter here.
    filterable: false,
    sortable: false,
    valueGetter: (_: any, row: any) => {
      const {costNum} = getCostsFromCellParams(row);
      return costNum;
    },
    renderCell: cellParams => {
      if (costsLoading) {
        return <LoadingDots />;
      }
      if (costsHasError) {
        return (
          <div className="flex h-full w-full items-center justify-center">
            <StatusChip
              value={ComputedCallStatuses.error}
              tooltipOverride="There was an error fetching the cost for this call."
            />
          </div>
        );
      }
      const {cost, costToolTipContent} = getCostsFromCellParams(cellParams.row);
      return (
        <Tooltip trigger={<div>{cost}</div>} content={costToolTipContent} />
      );
    },
  });

  cols.push({
    field: 'summary.weave.latency_ms',
    headerName: 'Latency',
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    // Should probably have a custom filter here.
    filterable: false,
    sortable: true,
    valueGetter: (_: any, row: any) => {
      if (flattenedTraceCallStatusCode(row) === ComputedCallStatuses.running) {
        // Call is still in progress, latency will be 0.
        // Displaying nothing seems preferable to being misleading.
        return null;
      }
      return traceCallLatencyS(row);
    },
    renderCell: cellParams => {
      if (
        flattenedTraceCallStatusCode(cellParams.row) ===
        ComputedCallStatuses.running
      ) {
        // Call is still in progress, latency will be 0.
        // Displaying nothing seems preferable to being misleading.
        return null;
      }
      const timeMs = traceCallLatencyMs(cellParams.row.__traceCall__);
      const timeString = monthRoundedTime(
        traceCallLatencyS(cellParams.row.__traceCall__)
      );
      return (
        <CellFilterWrapper
          onUpdateFilter={onUpdateFilter}
          field="summary.weave.latency_ms"
          rowId={cellParams.id.toString()}
          operation={'(number): >='}
          value={timeMs}>
          <div>{timeString}</div>
        </CellFilterWrapper>
      );
    },
  });

  if (storageSizeInfo) {
    cols.push({
      field: 'total_storage_size_bytes',
      headerName: 'Trace Size',
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      align: 'right',
      headerAlign: 'right',
      // filtering and sorting are expensive for this column, hence disabled
      filterable: false,
      sortable: false,
      renderCell: cellParams => {
        if (storageSizeInfo.storageSizeLoading) {
          return <LoadingDots />;
        }
        if (storageHasError) {
          return (
            <div className="flex h-full w-full items-center justify-center">
              <StatusChip
                value={ComputedCallStatuses.error}
                tooltipOverride="There was an error fetching the storage size for this call."
              />
            </div>
          );
        }
        const storageSize =
          cellParams.row.__traceCall__.total_storage_size_bytes ?? null;
        return (
          <div>{storageSize !== null ? convertBytes(storageSize) : ''}</div>
        );
      },
    });
  }

  cols.push({
    field: 'wb_run_id',
    width: 150,
    minWidth: 150,
    headerName: 'Run',
    sortable: false,
    filterable: false,
    renderCell: cellParams => {
      const runId = cellParams.row.__traceCall__.wb_run_id;
      return (
        <RunIdCellWithColor
          runId={runId}
          onUpdateFilter={onUpdateFilter}
          rowId={cellParams.id.toString()}
        />
      );
    },
  });

  cols.push({
    field: 'wb_run_step',
    width: 100,
    minWidth: 80,
    headerName: 'Run Step',
    sortable: true,
    filterable: false,
    renderCell: cellParams => {
      const runStep = cellParams.row.__traceCall__.wb_run_step;
      if (runStep == null) {
        return null;
      }
      return <span>{runStep}</span>;
    },
  });

  cols.forEach(col => {
    if (col.field in userDefinedColumnWidths) {
      col.width = userDefinedColumnWidths[col.field];
      col.flex = 0;
    }
  });

  // TODO: It would be better to build up the cols rather than throwing away
  //       some at the end, but making simpler change for now.
  let orderedCols = cols;
  if (allowedColumnPatterns !== undefined) {
    orderedCols = allowedColumnPatterns.flatMap(shownCol => {
      if (shownCol.includes('*')) {
        const regex = new RegExp('^' + shownCol.replace('*', '.*') + '$');
        return cols.filter(col => regex.test(col.field));
      } else {
        return cols.filter(col => col.field === shownCol);
      }
    });
  }

  return {cols: orderedCols, colGroupingModel: groupingModel};
}
/**
 * This function maintains an ever-growing list of dynamic column names. It is used to
 * determine which dynamic columns (e.g. attributes, inputs, outputs) are present in the
 * table data. If we page/filter/sort we don't want to lose the columns that were present
 * in the previous data.
 */
const useAllDynamicColumnNames = (
  tableData: FlattenedCallData[],
  shouldIgnoreColumn: (col: string) => boolean,
  resetDep: any,
  maxColumns?: number
) => {
  const prevColumnsRef = useRef<string[]>([]);
  const memoedDep = useDeepMemo(resetDep);
  const prevResetDepRef = useRef<any>(memoedDep);

  // If resetDep changed, clear the previous columns
  if (prevResetDepRef.current !== memoedDep) {
    prevColumnsRef.current = [];
    prevResetDepRef.current = memoedDep;
  }

  // Start with any previous columns
  const allDynamicColumnNames = useMemo(() => {
    let currentColumnPaths: Path[] = prevColumnsRef.current
      .filter(c => !shouldIgnoreColumn(c))
      .map(stringToPath);

    // Add new columns from the current table data
    for (let i = 0; i < tableData.length; i++) {
      const row = tableData[i];
      const keys = Object.keys(row);
      for (let j = 0; j < keys.length; j++) {
        const key = keys[j];
        const keyAsPath = stringToPath(key);
        if (isDynamicCallColumn(keyAsPath)) {
          currentColumnPaths = insertPath(currentColumnPaths, keyAsPath);
        }
        if (maxColumns && currentColumnPaths.length >= maxColumns) {
          break;
        }
      }
      if (maxColumns && currentColumnPaths.length >= maxColumns) {
        break;
      }
    }

    // Convert paths back to strings
    return currentColumnPaths.map(pathToString);
  }, [tableData, shouldIgnoreColumn, maxColumns]);

  // Store the current columns for the next render
  prevColumnsRef.current = allDynamicColumnNames;

  return allDynamicColumnNames;
};

const refIsExpandable = (ref: string): boolean => {
  if (!isWeaveRef(ref)) {
    return false;
  }
  const parsed = parseRef(ref);
  if (isWeaveObjectRef(parsed)) {
    return (
      parsed.weaveKind === 'object' ||
      // parsed.weaveKind === 'op' ||
      (parsed.weaveKind === 'table' &&
        parsed.artifactRefExtra != null &&
        parsed.artifactRefExtra.length > 0)
    );
  }
  return false;
};
